import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Note as NoteIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';

import { PlaceContext, DropPoint, CreateLocationNoteRequest, LocationNote } from '@m4a3/api-client';
import { orderfulApi } from '../../../services/api-instances';
import { getLoggedInUserName } from '../../../services/amplify-auth';
import { normalizeDropPoints } from '../../../utils/cellDropPointUtils';
import { RiskAssessment, RiskUtils } from '../utils/riskUtils';
import L from 'leaflet';

interface LocationPanelProps {
  locationContext: PlaceContext;
  riskAssessment?: RiskAssessment | null;
  isLoadingRiskAssessment?: boolean;
  newPinLocation?: L.LatLng | null;
  clickMode?: 'select' | 'add-drop-point';
  selectedDropPointId?: string | null;
  onAddPin?: () => void;
  onSavePin?: () => void;
  onCancelPin?: () => void;
  onDropPointSelect: (dropPointId: string | null) => void;
  onDropPointDelete: (dropPoint: DropPoint) => void;
  onDropPointEvaluate: (dropPoint: DropPoint) => void; // Callback to evaluate drop point risk
  onDropPointSetPreferred: (dropPoint: DropPoint) => void; // Callback to set drop point as preferred
  onNoteCreated: () => void; // Callback to refresh place context after note creation
  onPlaceStatusUpdate?: (placeId: string, enabled: boolean) => void; // Callback to update place status
  onClose: () => void;
}

const LocationPanel = ({
  locationContext,
  riskAssessment,
  isLoadingRiskAssessment,
  newPinLocation,
  clickMode,
  selectedDropPointId,
  onAddPin,
  onSavePin,
  onCancelPin,
  onDropPointSelect,
  onDropPointDelete,
  onDropPointEvaluate,
  onDropPointSetPreferred,
  onNoteCreated,
  onPlaceStatusUpdate,
  onClose,
}: LocationPanelProps): JSX.Element => {
  const { place, dropPoints, notes } = locationContext;
  
  // State for note creation
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState<LocationNote['noteType']>('general');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  // Handle both array and object formats for drop_points
  const safeDropPoints = normalizeDropPoints(dropPoints);
  const safeLocationNotes = Array.isArray(notes) ? notes : [];

  // Check if a drop point is the preferred one
  const isPreferredDropPoint = (dropPointId: string): boolean => {
    // Check if the place has a preferredDropPointId field (it might be dynamically added)
    return (place as any)?.preferredDropPointId === dropPointId;
  };

  // Get risk level color and label
  const getRiskInfo = (risk: number) => {
    const riskMap = {
      1: { color: 'success', label: 'Very Low' },
      2: { color: 'info', label: 'Low' },
      3: { color: 'warning', label: 'Medium' },
      4: { color: 'error', label: 'High' },
      5: { color: 'error', label: 'Very High' },
    };
    return riskMap[risk as keyof typeof riskMap] || { color: 'default', label: 'Unknown' };
  };

  // Format note type for display
  const formatNoteType = (noteType: string) => {
    return noteType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Handle note creation
  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      setNoteError('Note text is required');
      return;
    }

    setIsSavingNote(true);
    setNoteError(null);

    try {
      const createdBy =
        (await getLoggedInUserName().catch(() => undefined)) || 'unknown_user';

      const noteData: CreateLocationNoteRequest = {
        noteText: noteText.trim(),
        noteType: noteType,
        createdBy,
      };

      await orderfulApi.addPlaceNote(locationContext.placeId, noteData);
      
      // Reset form
      setNoteText('');
      setNoteType('general');
      setIsAddingNote(false);
      
      // Trigger refresh of location context
      onNoteCreated();
      
    } catch (error) {
      console.error('Failed to create note:', error);
      setNoteError('Failed to create note. Please try again.');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleCancelNote = () => {
    setNoteText('');
    setNoteType('general');
    setIsAddingNote(false);
    setNoteError(null);
  };

  return (
    <Box className="h-full flex flex-col">
      {/* Header */}
      <Box className="p-4 border-b border-gray-200 flex items-center justify-between">
        <Typography variant="h6" className="flex items-center space-x-2">
          <BusinessIcon />
          <span>
            {clickMode === 'add-drop-point' ? 'Adding Drop Point' : 'Place Details'}
          </span>
        </Typography>
        <Box className="flex items-center space-x-2">
          {clickMode === 'add-drop-point' && onCancelPin && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={onCancelPin}
            >
              Cancel
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Place Information */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" className="font-semibold mb-2">
              Place Information
            </Typography>
            <Box className="space-y-2">
              <Typography variant="body2">
                <strong>ID:</strong> {place.id}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {place.placeType}
              </Typography>
              <Typography variant="body2">
                <strong>Area:</strong> {place.areaSqm != null ? `${place.areaSqm.toFixed(1)} m²` : 'Unknown'}
              </Typography>
              {place.friendlyName && (
                <Typography variant="body2">
                  <strong>Name:</strong> {place.friendlyName}
                </Typography>
              )}
              <Box className="flex items-center space-x-2 mt-2">
                <Typography variant="body2" className="font-medium">
                  <strong>Status:</strong>
                </Typography>
                <Chip
                  size="small"
                  label={place.enabled ? 'Enabled' : 'Disabled'}
                  color={place.enabled ? 'success' : 'error'}
                  variant="outlined"
                  icon={place.enabled ? <CheckCircleIcon /> : <WarningIcon />}
                  onClick={onPlaceStatusUpdate ? () => onPlaceStatusUpdate(place.id, !place.enabled) : undefined}
                  clickable={!!onPlaceStatusUpdate}
                  sx={{
                    cursor: onPlaceStatusUpdate ? 'pointer' : 'default',
                    '&:hover': onPlaceStatusUpdate ? {
                      backgroundColor: place.enabled ? 'success.light' : 'error.light',
                      opacity: 0.8,
                    } : {}
                  }}
                />
              </Box>
              {place.distanceMeters != null && (
                <Typography variant="body2">
                  <strong>Distance:</strong> {place.distanceMeters.toFixed(1)}m
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Combined New Drop Point & Safety Assessment Section */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" className="font-semibold mb-3">
              {clickMode === 'add-drop-point' && newPinLocation ? 'New Drop Point & Safety Assessment' : 'Safety Assessment'}
            </Typography>
            
            {/* New Pin Coordinates Section */}
            {clickMode === 'add-drop-point' && newPinLocation && (
              <Box className="space-y-3 mb-4">
                <Typography variant="body2" color="text.secondary">
                  Position the pin by dragging or clicking on the map to place the drop point.
                </Typography>
                
                <Box className="p-3 bg-gray-50 rounded-lg">
                  <Typography variant="body2" className="font-medium mb-1">
                    Current Coordinates
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {newPinLocation.lat.toFixed(7)}, {newPinLocation.lng.toFixed(7)}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Safety Assessment Content */}
            {isLoadingRiskAssessment ? (
              <Box className="flex items-center justify-center py-4">
                <CircularProgress size={24} />
                <Typography variant="body2" className="ml-2">
                  Evaluating location safety...
                </Typography>
              </Box>
            ) : riskAssessment ? (
              <Box className="space-y-3">
                {/* Overall Risk Status */}
                <Box className="p-3 rounded-lg" style={{
                  backgroundColor: riskAssessment.overallRisk === 'HIGH' ? '#fee2e2' : 
                                 riskAssessment.overallRisk === 'LOW' ? '#ecfdf5' : '#f9fafb',
                  border: `1px solid ${riskAssessment.overallRisk === 'HIGH' ? '#fecaca' : 
                                      riskAssessment.overallRisk === 'LOW' ? '#bbf7d0' : '#e5e7eb'}`
                }}>
                  <Box className="flex items-center space-x-2 mb-2">
                    <Typography variant="body2" className="font-semibold">
                      {RiskUtils.getRiskEmoji(riskAssessment.overallRisk)} Overall Risk: {riskAssessment.overallRisk}
                    </Typography>
                    {!riskAssessment.canSave && (
                      <Chip
                        size="small"
                        icon={<WarningIcon />}
                        label="Cannot Save"
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {riskAssessment.message}
                  </Typography>
                </Box>

                {/* Detailed Risk Breakdown */}
                <Box className="grid grid-cols-2 gap-3">
                  <Box>
                    <Typography variant="caption" color="text.secondary" className="font-medium">
                      Ground Risk
                    </Typography>
                    <Box className="flex items-center space-x-1 mt-1">
                      <Typography variant="body2" className={RiskUtils.getRiskColor(riskAssessment.groundRisk)}>
                        {RiskUtils.getRiskEmoji(riskAssessment.groundRisk)} {riskAssessment.groundRisk}
                      </Typography>
                    </Box>
                    {riskAssessment.heightData != null && (
                      <Typography variant="caption" color="text.secondary">
                        Height: {riskAssessment.heightData.toFixed(1)}m
                      </Typography>
                    )}
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" className="font-medium">
                      Building Risk
                    </Typography>
                    <Box className="flex items-center space-x-1 mt-1">
                      <Typography variant="body2" className={RiskUtils.getRiskColor(riskAssessment.buildingRisk)}>
                        {RiskUtils.getRiskEmoji(riskAssessment.buildingRisk)} {riskAssessment.buildingRisk}
                      </Typography>
                    </Box>
                    {riskAssessment.buildingReason && (
                      <Typography variant="caption" color="text.secondary">
                        {riskAssessment.buildingReason}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Save and Cancel Buttons for New Pin */}
                {clickMode === 'add-drop-point' && newPinLocation && onSavePin && (
                  <Box className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={onCancelPin}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color={riskAssessment.canSave ? "primary" : "error"}
                      size="small"
                      disabled={!riskAssessment.canSave || isLoadingRiskAssessment}
                      onClick={onSavePin}
                      startIcon={isLoadingRiskAssessment ? <CircularProgress size={16} /> : undefined}
                    >
                      {isLoadingRiskAssessment ? 'Assessing...' : riskAssessment.canSave ? 'Save Drop Point' : 'Cannot Save'}
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" className="text-center py-4">
                {clickMode === 'add-drop-point' ? 'Assessment will appear after pin placement' : 'Click "Add Pin" to assess drop point safety for this location'}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Drop Points Section */}
        <Card>
          <CardContent>
            <Box className="flex items-center justify-between mb-3">
              <Typography variant="subtitle1" className="font-semibold flex items-center space-x-1">
                <LocationIcon />
                <span>Drop Points ({safeDropPoints.length})</span>
              </Typography>
              {clickMode === 'select' && onSavePin && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Start pin add mode at place centroid
                    if (onAddPin) {
                      onAddPin();
                    }
                  }}
                >
                  Add Pin
                </Button>
              )}
            </Box>
            {clickMode === 'select' && (
                          <Typography variant="body2" color="text.secondary" className="mb-3">
              Click "Add Pin" to start placing a new drop point for this place
            </Typography>
            )}

            {safeDropPoints.length === 0 ? (
              <Typography variant="body2" color="text.secondary" className="text-center py-4">
                {clickMode === 'add-drop-point' 
                  ? 'Position the pin on the map and save to create the first drop point.'
                  : 'No drop points defined for this location.'
                }
              </Typography>
            ) : (
              <List dense>
                {safeDropPoints.map((dropPoint, index) => {
                  const riskInfo = getRiskInfo(dropPoint.risk);
                  const isSelected = dropPoint.id === selectedDropPointId;
                  
                  return (
                    <React.Fragment key={dropPoint.id}>
                      <ListItem 
                        divider={index < safeDropPoints.length - 1}
                        {...(!dropPoint.isDeleted && { button: true })}
                        selected={isSelected && !dropPoint.isDeleted}
                        onClick={() => {
                          if (!dropPoint.isDeleted) {
                            onDropPointSelect(isSelected ? null : dropPoint.id);
                          }
                        }}
                        sx={{ 
                          cursor: dropPoint.isDeleted ? 'default' : 'pointer',
                          backgroundColor: isSelected && !dropPoint.isDeleted ? 'action.selected' : 'transparent',
                          opacity: dropPoint.isDeleted ? 0.5 : 1,
                          '&:hover': {
                            backgroundColor: dropPoint.isDeleted ? 'transparent' : 'action.hover',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <div 
                              style={{
                                width: isSelected ? 20 : 16,
                                height: isSelected ? 20 : 16,
                                borderRadius: '50%',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                backgroundColor: dropPoint.isDeleted 
                                  ? '#6B7280' 
                                  : dropPoint.verified 
                                    ? '#8B5CF6' 
                                    : '#3B82F6',
                                opacity: dropPoint.isDeleted ? 0.6 : 1,
                                transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                                transition: 'all 0.2s ease-in-out'
                              }}
                            />
                            {isPreferredDropPoint(dropPoint.id) && (
                              <StarIcon 
                                sx={{ 
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  color: '#FFD700',
                                  fontSize: 16,
                                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                                }} 
                              />
                            )}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box className="flex flex-col space-y-1">
                              <Typography variant="body2" className="font-medium">
                                {dropPoint.latitude.toFixed(7)}, {dropPoint.longitude.toFixed(7)}
                              </Typography>
                              <Box className="flex items-center space-x-2">
                                {dropPoint.isDeleted ? (
                                  <Chip
                                    size="small"
                                    label="Deleted"
                                    color="error"
                                    variant="filled"
                                  />
                                ) : (
                                  <>
                                    <Chip
                                      size="small"
                                      label={`Risk: ${riskInfo.label}`}
                                      color={riskInfo.color as any}
                                      variant="outlined"
                                    />
                                    {dropPoint.verified ? (
                                      <Chip
                                        size="small"
                                        icon={<CheckCircleIcon />}
                                        label="Verified"
                                        color="success"
                                        variant="outlined"
                                      />
                                    ) : (
                                      <Chip
                                        size="small"
                                        icon={<WarningIcon />}
                                        label="Unverified"
                                        color="warning"
                                        variant="outlined"
                                      />
                                    )}
                                  </>
                                )}
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Deliveries: {dropPoint.deliveryCount || 0}
                            </Typography>
                          }
                        />
                      </ListItem>
                      
                      {/* Expanded options when selected (only for non-deleted pins) */}
                      {isSelected && !dropPoint.isDeleted && (
                        <ListItem>
                          <Box className="w-full pl-4 pr-2 py-2">
                            <Typography variant="subtitle2" className="mb-2 font-semibold">
                              Drop Point Actions
                            </Typography>
                            <Box className="flex flex-wrap gap-2">
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<WarningIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDropPointEvaluate(dropPoint);
                                }}
                              >
                                Re-evaluate
                              </Button>
                              <Button
                                size="small"
                                variant={isPreferredDropPoint(dropPoint.id) ? "contained" : "outlined"}
                                color={isPreferredDropPoint(dropPoint.id) ? "primary" : "secondary"}
                                startIcon={isPreferredDropPoint(dropPoint.id) ? <StarIcon /> : <StarBorderIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDropPointSetPreferred(dropPoint);
                                }}
                              >
                                {isPreferredDropPoint(dropPoint.id) ? "Preferred" : "Set as Preferred"}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CloseIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDropPointDelete(dropPoint);
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        </ListItem>
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Place Notes Section */}
        <Card>
          <CardContent>
            <Box className="flex items-center justify-between mb-3">
              <Typography variant="subtitle1" className="font-semibold flex items-center space-x-1">
                <NoteIcon />
                <span>Place Notes ({safeLocationNotes.length})</span>
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setIsAddingNote(true)}
                disabled={isAddingNote}
              >
                Add Note
              </Button>
            </Box>

            {/* Add Note Form */}
            {isAddingNote && (
              <Box className="mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <Typography variant="subtitle2" className="mb-2 font-semibold">
                  Add New Note
                </Typography>
                
                {noteError && (
                  <Alert severity="error" className="mb-2" onClose={() => setNoteError(null)}>
                    {noteError}
                  </Alert>
                )}
                
                <Box className="space-y-3">
                  <FormControl size="small" fullWidth>
                    <InputLabel>Note Type</InputLabel>
                    <Select
                      value={noteType}
                      onChange={(e) => setNoteType(e.target.value as LocationNote['noteType'])}
                      label="Note Type"
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="access_information">Access Information</MenuItem>
                      <MenuItem value="delivery_instructions">Delivery Instructions</MenuItem>
                      <MenuItem value="safety_consideration">Safety Consideration</MenuItem>
                      <MenuItem value="delivery_consideration">Delivery Consideration</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    label="Note Text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Enter your note here..."
                    disabled={isSavingNote}
                  />
                  
                  <Box className="flex items-center justify-end space-x-2">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCancelNote}
                      disabled={isSavingNote}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveNote}
                      disabled={isSavingNote || !noteText.trim()}
                      startIcon={isSavingNote ? <CircularProgress size={16} /> : undefined}
                    >
                      {isSavingNote ? 'Saving...' : 'Save Note'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            {safeLocationNotes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" className="text-center py-4">
                No notes available for this place.
                Click "Add Note" to provide additional information.
              </Typography>
            ) : (
              <List dense>
                {safeLocationNotes.map((note, index) => (
                  <ListItem key={note.id} divider={index < safeLocationNotes.length - 1}>
                    <ListItemIcon>
                      <NoteIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box className="space-y-2">
                          <Typography variant="body2" className="font-medium">
                            {note.noteText}
                          </Typography>
                          <Box className="flex items-center space-x-2">
                            <Chip
                              size="small"
                              label={formatNoteType(note.noteType)}
                              color="primary"
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              by {note.createdBy}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {new Date(note.createdAt).toLocaleDateString()}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LocationPanel;
